/**
 * OAuthCallbackPage — handles the Google OAuth redirect for the web flow.
 *
 * Google redirects to <origin>/oauth/callback?code=...&state=...
 * This page extracts the params, validates the state against sessionStorage,
 * exchanges the code for tokens via POST /api/v1/youtube/oauth/exchange,
 * and navigates home on success.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../hooks/useAuth';
import { exchangeOAuthCode } from '../services/youtubePublishApi';
import type { ExchangeOAuthResponse } from '../services/youtubePublishApi';

interface OAuthCallbackPageProps {
  onNavigateHome: () => void;
}

type CallbackState = 'connecting' | 'connected' | 'error';

export function OAuthCallbackPage({ onNavigateHome }: OAuthCallbackPageProps): JSX.Element {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<CallbackState>('connecting');
  const [channelInfo, setChannelInfo] = useState<ExchangeOAuthResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // The full-page redirect from Google reloads the SPA, so Firebase must
    // rehydrate the session before we can attach a bearer token. Wait for the
    // auth state to settle; firing the exchange early sends no Authorization
    // header and the backend rejects it with 401.
    if (authLoading) return;

    // Guard against React StrictMode double-invoke
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem('yt_oauth_state');
    const returnTo = sessionStorage.getItem('yt_oauth_return') ?? '/';

    // Validate required params
    if (!code || !state) {
      setErrorMessage('Missing code or state in callback URL.');
      setStatus('error');
      return;
    }

    // CSRF check
    if (!expectedState || state !== expectedState) {
      setErrorMessage('OAuth state mismatch — possible CSRF attempt. Please try again.');
      setStatus('error');
      return;
    }

    // Auth settled but no session — the exchange would 401. Surface a clear
    // message instead of a silent failure.
    if (!user) {
      setErrorMessage('Your session expired during sign-in. Please log in again and reconnect.');
      setStatus('error');
      return;
    }

    exchangeOAuthCode(code, state)
      .then((data) => {
        // Clear sessionStorage keys
        sessionStorage.removeItem('yt_oauth_state');
        sessionStorage.removeItem('yt_oauth_return');

        setChannelInfo(data);
        setStatus('connected');

        // Navigate home after brief success display
        setTimeout(() => {
          window.history.replaceState({}, '', returnTo);
          onNavigateHome();
        }, 1500);
      })
      .catch((err: unknown) => {
        setErrorMessage(
          err instanceof Error ? err.message : 'OAuth exchange failed — please try again.'
        );
        setStatus('error');
      });
  }, [onNavigateHome, authLoading, user]);

  return (
    <div className="min-h-screen bg-[#0a0015] flex items-center justify-center p-4">
      <div className="card max-w-md w-full flex flex-col items-center gap-6 p-8 bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl">
        {status === 'connecting' && (
          <>
            <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white/70 text-sm text-center">
              {t('youtube.oauth.callback.connecting')}
            </p>
          </>
        )}

        {status === 'connected' && channelInfo && (
          <>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/20 border border-green-500/30">
              <svg
                className="w-6 h-6 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-white text-sm text-center font-medium">
              {t('youtube.oauth.callback.connected', { channel: channelInfo.channel_title })}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/20 border border-red-500/30">
              <svg
                className="w-6 h-6 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-white/70 text-sm text-center">{t('youtube.oauth.callback.error')}</p>
            {errorMessage && (
              <p className="text-red-400 text-xs text-center break-words">{errorMessage}</p>
            )}
            <button
              onClick={onNavigateHome}
              className="min-h-[44px] px-6 py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 text-white text-sm font-medium transition-colors border border-purple-500/30"
            >
              {t('youtube.oauth.callback.back')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
